import { Injectable, signal, inject, computed } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CommonService } from '@seasidebeta/components';

export interface CrewLanguage {
  code: string;
  name: string;
  flag: string;
}

const STORAGE_KEY = 'crew-lang';

/**
 * Central service for managing the active language across the application.
 * Uses Angular Signals for reactivity.
 */
@Injectable({ providedIn: 'root' })
export class CrewTranslocoService {
  private translocoService: TranslocoService = inject(TranslocoService);
  private commonService: CommonService = inject(CommonService);

  /** All available languages with labels and flag emojis. */
  readonly availableLanguages: Array<CrewLanguage> = [
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  ];

  /** The currently active language code as a signal. */
  readonly activeLang = signal<string>(this.getInitialLang());

  /** The currently active language object. */
  readonly activeLanguage = computed<CrewLanguage>(() => {
    const code: string = this.activeLang();
    return this.availableLanguages.find(l => l.code === code) ?? this.availableLanguages[0];
  });

  constructor() {
    // Sync Transloco and CommonService with persisted language
    const initialLang: string = this.activeLang();
    if (this.translocoService.getActiveLang() !== initialLang) {
      this.translocoService.setActiveLang(initialLang);
    }
    this.commonService.currentLanguage = initialLang;
  }

  /**
   * Switch the active language at runtime. Updates Transloco, the signal, and persists to localStorage.
   */
  setLang(lang: string): void {
    this.translocoService.setActiveLang(lang);
    this.activeLang.set(lang);
    this.commonService.currentLanguage = lang;
    localStorage.setItem(STORAGE_KEY, lang);
  }

  private getInitialLang(): string {
    const stored: string | null = localStorage.getItem(STORAGE_KEY);
    if (stored && this.isValidLang(stored)) {
      return stored;
    }
    return 'no';
  }

  private isValidLang(lang: string): boolean {
    return this.availableLanguages.some(l => l.code === lang);
  }
}
